// src/services/aiPairingService.js
/**
 * AI Pairing Service
 * Advanced pairing intelligence system that combines beverage knowledge with food analysis
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "../firebase-config";
import aiService from "./aiService.js";

export class AIPairingService {
  constructor() {
    this.pairingsCollection = "ai_pairings";
    this.pairingAnalyticsCollection = "pairing_analytics";
    this.customerPreferencesCollection = "customer_preferences";
  }

  /**
   * Generate intelligent pairing recommendations for a specific beverage
   * @param {string} restaurantId - Restaurant ID
   * @param {object} beverage - Beverage object
   * @param {object} options - Pairing options
   */
  async generateBeveragePairings(restaurantId, beverage, options = {}) {
    try {
      console.log(`🍷 Generating AI pairings for: ${beverage.name}`);

      const {
        includeMenuItems = true,
        includeGenericPairings = true,
        personalization = null,
        maxSuggestions = 10,
        confidenceThreshold = 0.6,
      } = options;

      const pairings = [];

      // Get restaurant's food menu if available
      let menuItems = [];
      if (includeMenuItems) {
        menuItems = await this.getRestaurantMenuItems(restaurantId);
      }

      // Generate menu-specific pairings
      if (menuItems.length > 0) {
        const menuPairings = await this.generateMenuSpecificPairings(
          beverage,
          menuItems
        );
        pairings.push(...menuPairings);
      }

      // Generate generic pairing recommendations
      if (includeGenericPairings) {
        const genericPairings = await this.generateGenericPairings(beverage);
        pairings.push(...genericPairings);
      }

      // Apply personalization if provided
      if (personalization) {
        const personalizedPairings = await this.applyPersonalization(
          pairings,
          personalization
        );
        pairings.splice(0, pairings.length, ...personalizedPairings);
      }

      // Filter by confidence and sort
      const filteredPairings = pairings
        .filter((pairing) => pairing.confidence >= confidenceThreshold)
        .sort((a, b) => b.confidence - a.confidence)
        .slice(0, maxSuggestions);

      // Store pairings for analytics
      await this.storePairingRecommendations(
        restaurantId,
        beverage.id,
        filteredPairings
      );

      console.log(`✅ Generated ${filteredPairings.length} AI pairings`);
      return filteredPairings;
    } catch (error) {
      console.error("Error generating beverage pairings:", error);
      throw error;
    }
  }

  /**
   * Generate menu-specific pairings based on restaurant's actual food menu
   * @param {object} beverage - Beverage object
   * @param {Array} menuItems - Restaurant's menu items
   */
  async generateMenuSpecificPairings(beverage, menuItems) {
    const prompt = `
    Create specific food pairing recommendations for this beverage with the restaurant's actual menu items:

    BEVERAGE:
    Name: ${beverage.name}
    Category: ${beverage.category}
    Description: ${beverage.description}
    ${
      beverage.aiEnhanced
        ? `
    Tasting Notes: ${JSON.stringify(beverage.aiEnhanced.tastingNotes)}
    Style: ${beverage.aiEnhanced.style?.name}
    Producer: ${beverage.aiEnhanced.producer?.name}
    Region: ${beverage.aiEnhanced.producer?.location}
    `
        : ""
    }

    RESTAURANT MENU ITEMS:
    ${menuItems
      .map(
        (item) => `
    - ${item.name}: ${item.description || "No description"}
      Category: ${item.category || "Unknown"}
      Price: $${item.price || "N/A"}
    `
      )
      .join("")}

    For each viable pairing, return JSON array with:
    [
      {
        "dishId": "menu_item_id",
        "dishName": "Exact dish name from menu",
        "pairingType": "complement|contrast|bridge",
        "confidence": 0.85, // 0-1 confidence score
        "reasoning": "Detailed explanation of why they pair well",
        "flavorConnection": "How flavors work together",
        "recommendations": {
          "servingOrder": "before|with|after the meal",
          "temperature": "serving temperature notes",
          "glassware": "recommended glass if applicable",
          "portionSuggestion": "suggested serving size"
        },
        "experienceEnhancement": "How this pairing elevates the dining experience",
        "alternatives": ["alternative dishes that could also work"],
        "tags": ["romantic", "business", "casual", "celebration", etc]
      }
    ]

    Only include pairings with confidence >= 0.6. Focus on the best 5-8 pairings.
    `;

    const response = await aiService.generateContent(prompt);
    const pairings = JSON.parse(response);

    return pairings.map((pairing) => ({
      ...pairing,
      source: "menu-specific",
      generatedAt: new Date().toISOString(),
    }));
  }

  /**
   * Generate generic pairing recommendations for broader appeal
   * @param {object} beverage - Beverage object
   */
  async generateGenericPairings(beverage) {
    const prompt = `
    Create general food pairing recommendations for this beverage that any restaurant could potentially offer:

    BEVERAGE:
    Name: ${beverage.name}
    Category: ${beverage.category}
    Description: ${beverage.description}
    ${
      beverage.aiEnhanced
        ? `
    Tasting Notes: ${JSON.stringify(beverage.aiEnhanced.tastingNotes)}
    Style: ${beverage.aiEnhanced.style?.name}
    Producer: ${beverage.aiEnhanced.producer?.name}
    Region: ${beverage.aiEnhanced.producer?.location}
    `
        : ""
    }

    Return JSON array with generic food categories and specific examples:
    [
      {
        "foodCategory": "Seafood",
        "specificExamples": ["Grilled Salmon", "Oysters", "Lobster"],
        "pairingType": "complement|contrast|bridge",
        "confidence": 0.88,
        "reasoning": "Why this category pairs well",
        "cookingMethods": ["grilled", "poached", "raw"],
        "seasonings": ["herbs", "citrus", "butter"],
        "occasionSuitability": ["dinner", "special occasion", "casual"],
        "pricePointSuitability": ["fine dining", "casual dining", "all"],
        "cuisineStyles": ["Mediterranean", "French", "American"]
      }
    ]

    Include 6-10 food categories that pair excellently with this beverage.
    `;

    const response = await aiService.generateContent(prompt);
    const pairings = JSON.parse(response);

    return pairings.map((pairing) => ({
      ...pairing,
      source: "generic",
      generatedAt: new Date().toISOString(),
    }));
  }

  /**
   * Apply personalization to pairing recommendations
   * @param {Array} pairings - Base pairing recommendations
   * @param {object} personalization - User preferences and history
   */
  async applyPersonalization(pairings, personalization) {
    const prompt = `
    Personalize these pairing recommendations based on customer preferences:

    CUSTOMER PROFILE:
    Preferences: ${JSON.stringify(personalization.preferences)}
    Previous Orders: ${JSON.stringify(personalization.previousOrders)}
    Dietary Restrictions: ${JSON.stringify(personalization.dietaryRestrictions)}
    Occasion: ${personalization.occasion || "Not specified"}
    Budget Range: ${personalization.budgetRange || "Not specified"}
    Party Size: ${personalization.partySize || "Not specified"}

    CURRENT PAIRINGS:
    ${JSON.stringify(pairings)}

    Return the same pairing structure but:
    1. Adjust confidence scores based on preferences (+/- 0.1 to 0.3)
    2. Add personalization reasoning
    3. Reorder by personalized relevance
    4. Add personal recommendations in each pairing object

    Return as JSON array maintaining the original structure plus:
    {
      ...originalPairing,
      "personalizedConfidence": 0.92,
      "personalizationReasoning": "Why this is good for this specific customer",
      "personalRecommendation": "Specific suggestion for this customer"
    }
    `;

    const response = await aiService.generateContent(prompt);
    return JSON.parse(response);
  }

  /**
   * Get restaurant's menu items for pairing analysis
   * @param {string} restaurantId - Restaurant ID
   */
  async getRestaurantMenuItems(restaurantId) {
    try {
      // First try to get analyzed food menu
      const analysisRef = doc(db, "menu_analysis", `${restaurantId}_full`);
      const analysisSnap = await getDoc(analysisRef);

      if (analysisSnap.exists()) {
        const analysisData = analysisSnap.data();
        if (analysisData.dishes && analysisData.dishes.length > 0) {
          return analysisData.dishes;
        }
      }

      // Fallback to basic menu items if no analyzed food menu
      const menuQuery = query(
        collection(db, "menu_items"),
        where("restaurantId", "==", restaurantId)
      );

      const snapshot = await getDocs(menuQuery);
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error("Error getting restaurant menu items:", error);
      return [];
    }
  }

  /**
   * Generate cocktail recommendations using available spirits
   * @param {string} restaurantId - Restaurant ID
   * @param {object} options - Generation options
   */
  async generateCocktailRecommendations(restaurantId, options = {}) {
    try {
      console.log(
        `🍸 Generating cocktail recommendations for restaurant: ${restaurantId}`
      );

      const {
        occasion = "general",
        skillLevel = "intermediate",
        maxRecipes = 10,
        includeClassics = true,
        includeOriginal = true,
      } = options;

      // Get restaurant's spirit inventory
      const spiritsQuery = query(
        collection(db, "menu_items"),
        where("restaurantId", "==", restaurantId),
        where("category", "==", "spirit")
      );

      const spiritsSnapshot = await getDocs(spiritsQuery);
      const availableSpirits = spiritsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      if (availableSpirits.length === 0) {
        return [];
      }

      const cocktailRecommendations = await this.generateCocktailsFromSpirits(
        availableSpirits,
        { occasion, skillLevel, maxRecipes, includeClassics, includeOriginal }
      );

      // Store recommendations
      await this.storeCocktailRecommendations(
        restaurantId,
        cocktailRecommendations
      );

      console.log(
        `✅ Generated ${cocktailRecommendations.length} cocktail recommendations`
      );
      return cocktailRecommendations;
    } catch (error) {
      console.error("Error generating cocktail recommendations:", error);
      throw error;
    }
  }

  /**
   * Generate cocktail recipes from available spirits
   * @param {Array} spirits - Available spirits
   * @param {object} options - Generation options
   */
  async generateCocktailsFromSpirits(spirits, options) {
    const prompt = `
    Create cocktail recommendations using these available spirits:

    AVAILABLE SPIRITS:
    ${spirits
      .map(
        (spirit) => `
    - ${spirit.name}: ${spirit.description || "No description"}
      Category: ${spirit.category}
      ${spirit.aiEnhanced ? `Style: ${spirit.aiEnhanced.style?.name}` : ""}
    `
      )
      .join("")}

    OPTIONS:
    Occasion: ${options.occasion}
    Skill Level: ${options.skillLevel}
    Include Classics: ${options.includeClassics}
    Include Original: ${options.includeOriginal}

    Return JSON array with cocktail recipes:
    [
      {
        "name": "Cocktail Name",
        "type": "classic|modern|original",
        "baseSpirit": "primary spirit from available list",
        "spiritsUsed": ["spirit1", "spirit2"],
        "ingredients": [
          {
            "name": "Ingredient name",
            "amount": "2 oz",
            "type": "spirit|mixer|garnish|other",
            "isAvailable": true,
            "substitute": "alternative if not available"
          }
        ],
        "instructions": ["Step 1", "Step 2", "Step 3"],
        "glassware": "rocks|coupe|highball|etc",
        "garnish": "lemon twist",
        "difficulty": "easy|medium|hard",
        "preparationTime": "2 minutes",
        "flavorProfile": ["smooth", "citrusy", "bitter"],
        "occasion": ["dinner", "aperitif", "nightcap"],
        "description": "Brief description of the cocktail",
        "history": "Brief history if classic cocktail",
        "variations": ["variation 1", "variation 2"],
        "pairingNotes": "What foods pair well with this cocktail"
      }
    ]

    Generate ${
      options.maxRecipes
    } recipes focusing on cocktails that showcase the available spirits.
    `;

    const response = await aiService.generateContent(prompt);
    return JSON.parse(response);
  }

  /**
   * Analyze pairing performance and learn from customer feedback
   * @param {string} restaurantId - Restaurant ID
   * @param {string} pairingId - Pairing ID
   * @param {object} feedback - Customer feedback
   */
  async analyzePairingPerformance(restaurantId, pairingId, feedback) {
    try {
      const analyticsRef = doc(
        db,
        this.pairingAnalyticsCollection,
        `${restaurantId}_${pairingId}`
      );

      // Get existing analytics or create new
      const analyticsSnap = await getDoc(analyticsRef);
      let analytics = analyticsSnap.exists()
        ? analyticsSnap.data()
        : {
            pairingId,
            restaurantId,
            totalFeedback: 0,
            averageRating: 0,
            feedbackHistory: [],
            createdAt: new Date().toISOString(),
          };

      // Add new feedback
      analytics.feedbackHistory.push({
        ...feedback,
        timestamp: new Date().toISOString(),
      });

      // Recalculate analytics
      analytics.totalFeedback = analytics.feedbackHistory.length;
      analytics.averageRating =
        analytics.feedbackHistory.reduce((sum, f) => sum + f.rating, 0) /
        analytics.totalFeedback;
      analytics.updatedAt = new Date().toISOString();

      // Store updated analytics
      await setDoc(analyticsRef, analytics);

      // Use feedback to improve future recommendations
      await this.updatePairingModel(restaurantId, pairingId, feedback);

      console.log(
        `📊 Updated pairing analytics: ${analytics.averageRating}/5 (${analytics.totalFeedback} reviews)`
      );
      return analytics;
    } catch (error) {
      console.error("Error analyzing pairing performance:", error);
      throw error;
    }
  }

  /**
   * Update pairing model based on feedback (machine learning component)
   * @param {string} restaurantId - Restaurant ID
   * @param {string} pairingId - Pairing ID
   * @param {object} feedback - Customer feedback
   */
  async updatePairingModel(restaurantId, pairingId, feedback) {
    // This would connect to a machine learning pipeline
    // For now, we'll store the learning data for future model training

    const learningRef = doc(
      db,
      "pairing_learning_data",
      `${restaurantId}_${Date.now()}`
    );
    await setDoc(learningRef, {
      restaurantId,
      pairingId,
      feedback,
      context: {
        timestamp: new Date().toISOString(),
        dayOfWeek: new Date().getDay(),
        timeOfDay: new Date().getHours(),
      },
    });
  }

  /**
   * Store pairing recommendations for tracking and analytics
   * @param {string} restaurantId - Restaurant ID
   * @param {string} beverageId - Beverage ID
   * @param {Array} pairings - Generated pairings
   */
  async storePairingRecommendations(restaurantId, beverageId, pairings) {
    try {
      const recommendationsRef = doc(
        db,
        this.pairingsCollection,
        `${restaurantId}_${beverageId}`
      );
      await setDoc(recommendationsRef, {
        restaurantId,
        beverageId,
        pairings,
        generatedAt: new Date().toISOString(),
        totalPairings: pairings.length,
        averageConfidence:
          pairings.reduce((sum, p) => sum + p.confidence, 0) / pairings.length,
      });
    } catch (error) {
      console.error("Error storing pairing recommendations:", error);
    }
  }

  /**
   * Store cocktail recommendations
   * @param {string} restaurantId - Restaurant ID
   * @param {Array} cocktails - Generated cocktail recommendations
   */
  async storeCocktailRecommendations(restaurantId, cocktails) {
    try {
      const cocktailsRef = doc(db, "cocktail_recommendations", restaurantId);
      await setDoc(cocktailsRef, {
        restaurantId,
        cocktails,
        generatedAt: new Date().toISOString(),
        totalCocktails: cocktails.length,
      });
    } catch (error) {
      console.error("Error storing cocktail recommendations:", error);
    }
  }

  /**
   * Get existing pairing recommendations
   * @param {string} restaurantId - Restaurant ID
   * @param {string} beverageId - Beverage ID
   */
  async getPairingRecommendations(restaurantId, beverageId) {
    try {
      const recommendationsRef = doc(
        db,
        this.pairingsCollection,
        `${restaurantId}_${beverageId}`
      );
      const recommendationsSnap = await getDoc(recommendationsRef);

      if (!recommendationsSnap.exists()) {
        return null;
      }

      return recommendationsSnap.data();
    } catch (error) {
      console.error("Error getting pairing recommendations:", error);
      return null;
    }
  }

  /**
   * Get trending pairings across the platform
   * @param {object} options - Query options
   */
  async getTrendingPairings(options = {}) {
    try {
      const {
        timeframe = 30, // days
        limit: maxResults = 20,
      } = options;

      const analyticsQuery = query(
        collection(db, this.pairingAnalyticsCollection),
        where(
          "updatedAt",
          ">=",
          new Date(Date.now() - timeframe * 24 * 60 * 60 * 1000).toISOString()
        ),
        orderBy("averageRating", "desc"),
        limit(maxResults)
      );

      const snapshot = await getDocs(analyticsQuery);
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error("Error getting trending pairings:", error);
      return [];
    }
  }
}

export const aiPairingService = new AIPairingService();
